/**
 * **Die Erzeugungszähler einer Anlage und was hinter ihnen steht** (#42) — die
 * Gliederung der Seite „Erzeugungsanlagen" und die Auswahl „Zähler" im Dialog
 * einer Anlage.
 *
 * Das Modell (#40, ADR 0038 Nachtrag): PV-Anlagen und Speicher stehen hinter
 * einem Zähler. **Der Zähler ist die Wahrheit** über das, was aus ihnen heraus-
 * und in sie hineingeht (netto), ihre Geräte liefern die Bruttowerte. Ein
 * Erzeugungszähler ist deshalb, was eine Anlage messen kann:
 *
 * 1. jeder Zähler in einem Steckplatz, der im gültigen Messkonzept Erzeugung
 *    liefert (`isGenerationSlot`) — zuerst und in der Reihenfolge der Vorlage,
 * 2. jeder virtuelle Zähler und jeder Zähler der Bauart `generation`, auch wenn
 *    er noch in keinem Steckplatz steckt (ein Shelly am Balkonkraftwerk),
 * 3. jede Messstelle, hinter der schon eine Anlage steht — sonst verschwände
 *    eine Zuordnung aus der Liste, nur weil ihr Zähler kein Erzeugungszähler
 *    mehr ist.
 *
 * Reine Rechnung ohne React.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import { isGenerationSlot } from "./concept-roles.js";
import { assignmentOf, meteringPointOfSlot } from "./concept-editor.js";
import { meterNumberText } from "./meter-labels.js";

type Concept = ApiComponents["schemas"]["MeasurementConcept"];
type Template = ApiComponents["schemas"]["ConceptTemplate"];
type Meter = ApiComponents["schemas"]["Meter"];
type GenerationAsset = ApiComponents["schemas"]["GenerationAsset"];

export interface GenerationMeterGroup {
  readonly meteringPointId: string;
  /** Der Zähler, der heute dort hängt — `null`, wenn an der Messstelle keiner mehr hängt. */
  readonly meter: Meter | null;
  /** Der Steckplatz im gültigen Messkonzept — `null`, wenn der Zähler in keinem steckt. */
  readonly slotLabel: string | null;
  readonly assets: readonly GenerationAsset[];
}

export interface GenerationMeterLayout {
  readonly groups: readonly GenerationMeterGroup[];
  /** Anlagen hinter keinem Zähler — „nicht gemessen". */
  readonly unmetered: readonly GenerationAsset[];
}

const isCurrent = (meter: Meter): boolean => meter.status === "active" || meter.status === "draft";

/** Der Zähler, der heute an einer Messstelle hängt; sonst der letzte, der dort hing. */
export function meterAtPoint(meters: readonly Meter[], meteringPointId: string): Meter | null {
  return (
    meters.find((m) => m.meteringPointId === meteringPointId && isCurrent(m)) ??
    meters.find((m) => m.meteringPointId === meteringPointId) ??
    null
  );
}

export function generationMeterLayout(input: {
  readonly concept: Pick<Concept, "nodes"> | null;
  readonly template: Pick<Template, "inputs" | "slots"> | null;
  readonly meters: readonly Meter[];
  readonly assets: readonly GenerationAsset[];
}): GenerationMeterLayout {
  const { concept, template, meters, assets } = input;
  const order: string[] = [];
  const slotLabels = new Map<string, string>();
  const add = (meteringPointId: string) => {
    if (!order.includes(meteringPointId)) order.push(meteringPointId);
  };

  if (concept !== null && template !== null) {
    const assignment = assignmentOf(concept);
    for (const slot of template.slots.filter(isGenerationSlot)) {
      const meteringPointId = meteringPointOfSlot(template, slot, assignment);
      if (meteringPointId === null) continue;
      add(meteringPointId);
      if (!slotLabels.has(meteringPointId)) slotLabels.set(meteringPointId, slot.label);
    }
  }
  for (const meter of meters) {
    if (meter.commodity !== "electricity" || !isCurrent(meter)) continue;
    if (meter.virtual || meter.kind === "generation") add(meter.meteringPointId);
  }
  for (const asset of assets) {
    if (asset.meteringPointId !== null) add(asset.meteringPointId);
  }

  return {
    groups: order.map((meteringPointId) => ({
      meteringPointId,
      meter: meterAtPoint(meters, meteringPointId),
      slotLabel: slotLabels.get(meteringPointId) ?? null,
      assets: assets.filter((asset) => asset.meteringPointId === meteringPointId),
    })),
    unmetered: assets.filter((asset) => asset.meteringPointId === null),
  };
}

/**
 * Die Auswahl „Zähler" im Dialog einer Anlage: der Zähler, dahinter sein Steckplatz.
 * Tragen zwei denselben Namen (zwei Shellys „Balkon"), steht die Zählernummer
 * oder „Messwerk im Gerät" dabei — zwei gleiche Einträge ließen raten. Eine
 * Messstelle ohne Zähler heißt so und nicht „Zähler ohne Namen".
 */
export function generationMeterOptions(
  groups: readonly GenerationMeterGroup[],
): { value: string; label: string }[] {
  const names = groups.map((group) => group.meter?.label ?? null);
  return groups.map((group, index) => {
    const name = names[index];
    if (name === null || group.meter === null)
      return { value: group.meteringPointId, label: "Messstelle ohne Zähler" };
    const twin = names.filter((other) => other === name).length > 1;
    const parts = [
      twin ? `${name} (${meterNumberText(group.meter)})` : name,
      group.slotLabel,
    ].filter((part): part is string => part !== null);
    return { value: group.meteringPointId, label: parts.join(" · ") };
  });
}
